import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reportsApi, type AdminReportResponse } from '../api/client';

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatLabel(format: string) {
  const map: Record<string, string> = { pdf: 'PDF', excel: 'Excel', pptx: 'PowerPoint' };
  return map[format] ?? format;
}

function extensionFromContentType(contentType: string, outputFormat: string): string {
  const ct = contentType.toLowerCase();
  if (ct.includes('pdf')) return 'pdf';
  if (ct.includes('json')) return 'json';
  if (ct.includes('spreadsheet') || ct.includes('excel') || ct.includes('spreadsheetml')) return 'xlsx';
  if (ct.includes('presentation') || ct.includes('powerpoint') || ct.includes('presentationml')) return 'pptx';
  const fmt = (outputFormat || '').toLowerCase();
  if (fmt === 'excel') return 'xlsx';
  if (fmt === 'pptx') return 'pptx';
  if (fmt === 'pdf') return 'pdf';
  return 'bin';
}

/** ADM-S03 / ADM-S04 健康経営レポート（API 連携） */
export default function AdminReports() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [list, setList] = useState<AdminReportResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [outputFormat, setOutputFormat] = useState<string>('pdf');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);

  const loadList = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    reportsApi
      .list(token, { limit: 50 })
      .then((res) => setList(res.data ?? []))
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  function isCompleted(r: AdminReportResponse) {
    return (
      r.status === 'completed' ||
      r.status === 'complete' ||
      (r.completed_at != null && r.completed_at !== '')
    );
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token || !periodFrom || !periodTo) return;
    setCreateError(null);
    setCreating(true);
    reportsApi
      .create(
        {
          period_from: periodFrom,
          period_to: periodTo,
          output_format: outputFormat || 'pdf',
        },
        token
      )
      .then(() => {
        loadList();
        setPeriodFrom('');
        setPeriodTo('');
      })
      .catch((err) => {
        setCreateError(err instanceof Error ? err.message : t('common.error'));
      })
      .finally(() => setCreating(false));
  };

  const handleDownload = async (report: AdminReportResponse) => {
    const token = getToken();
    if (!token || !isCompleted(report)) return;
    setDownloadNotice(null);
    setDownloadingId(report.id);
    try {
      const { download_url: downloadUrl } = await reportsApi.getDownloadUrl(report.id, token);
      if (!downloadUrl) return;

      try {
        const fileRes = await fetch(downloadUrl, { method: 'GET', mode: 'cors', credentials: 'omit' });
        if (!fileRes.ok) throw new Error(String(fileRes.status));
        const blob = await fileRes.blob();
        const ct = fileRes.headers.get('content-type') || blob.type || '';
        const ext = extensionFromContentType(ct, report.output_format);
        const wanted = (report.output_format || 'pdf').toLowerCase();
        if (wanted === 'pdf' && ext === 'json') {
          setDownloadNotice(t('admin.reportsDownloadPdfGotJson'));
        }
        const filename = `health-report-${report.id}.${ext}`;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        setDownloadNotice(t('admin.reportsDownloadFallbackTab'));
      }
    } catch (err) {
      setDownloadNotice(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="page admin-reports-page">
      <h1>{t('admin.reports')}</h1>

      <form onSubmit={handleCreate} className="admin-card admin-form">
        <h3>{t('admin.reportsCreate')}</h3>
        <div className="admin-form-group">
          <label htmlFor="admin-reportsPeriodFrom">{t('admin.reportsPeriodFrom')}</label>
          <input id="admin-reportsPeriodFrom"
            type="date"
            value={periodFrom}
            onChange={(e) => setPeriodFrom(e.target.value)}
            required
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="admin-reportsPeriodTo">{t('admin.reportsPeriodTo')}</label>
          <input id="admin-reportsPeriodTo"
            type="date"
            value={periodTo}
            onChange={(e) => setPeriodTo(e.target.value)}
            required
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="admin-reportsFormat">{t('admin.reportsFormat')}</label>
          <select id="admin-reportsFormat" value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)}>
            <option value="pdf">PDF</option>
            <option value="excel" disabled>{t('admin.reportsFormatComingSoon', { format: 'Excel' })}</option>
            <option value="pptx" disabled>{t('admin.reportsFormatComingSoon', { format: 'PowerPoint' })}</option>
          </select>
          <p className="admin-form-hint">{t('admin.reportsFormatPdfOnlyNote')}</p>
        </div>
        {createError && <p className="admin-form-error">{createError}</p>}
        <button type="submit" className="admin-btn-primary" disabled={creating}>
          {creating ? t('common.loading') : t('admin.reportsStart')}
        </button>
      </form>

      <div className="admin-card">
        <h3>{t('admin.reportsList')}</h3>
        {downloadNotice && (
          <p className="admin-form-warning" role="status">
            {downloadNotice}
          </p>
        )}
        {error && (
          <>
            <p className="admin-form-error">{error}</p>
            <button type="button" className="dashboard-error-retry" onClick={loadList}>
              {t('dashboard.retry')}
            </button>
          </>
        )}
        {loading && <p>{t('common.loading')}</p>}
        {!loading && !error && (
          list.length === 0 ? (
            <p className="admin-empty">{t('admin.reportsEmpty')}</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.reportsPeriod')}</th>
                  <th scope="col">{t('admin.reportsFormat')}</th>
                  <th scope="col">{t('admin.status')}</th>
                  <th scope="col">{t('admin.reportsCompletedAt')}</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>{r.period_from} ～ {r.period_to}</td>
                    <td>{formatLabel(r.output_format)}</td>
                    <td>
                      <span className={`admin-badge ${isCompleted(r) ? 'admin-badge-ok' : ''}`}>{r.status}</span>
                    </td>
                    <td>{formatDateTime(r.completed_at)}</td>
                    <td>
                      {isCompleted(r) ? (
                        <button
                          type="button"
                          className="admin-btn-download"
                          onClick={() => handleDownload(r)}
                          disabled={downloadingId === r.id}
                        >
                          {downloadingId === r.id ? t('common.loading') : t('admin.reportsDownload')}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
      </p>
    </div>
  );
}
